// =============================================================================
// app/settings/notifications.tsx — per-category notification toggles.
//
// P1-PROFILE redesign: the flat single-list of 9 toggles is regrouped into three
// labelled sections — Delivery (email/push), Activity (mentions/replies/likes/
// follows/DMs), and Communities (invites/updates) — each on its own uniform glass
// card with clean dividers and token-driven toggles. Full-screen ParticleBackground
// replaces the flat lavender fill. All API/business logic (GET/PUT
// /settings/notifications) is unchanged.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Switch,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { GlassCard } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';

interface NotifSettings {
  email: boolean;
  push: boolean;
  mentions: boolean;
  replies: boolean;
  likes: boolean;
  follows: boolean;
  directMessages: boolean;
  groupInvites: boolean;
  communityUpdates: boolean;
}

type NotifItem = {
  key: keyof NotifSettings;
  icon: string;
  label: string;
  desc: string;
};

// Grouped by intent so the list reads as "how/what/where" instead of a flat wall
// of switches. Order within a group is most-common first.
const NOTIF_GROUPS: { title: string; subtitle: string; items: NotifItem[] }[] = [
  {
    title: 'Delivery',
    subtitle: 'How you hear from CRYB',
    items: [
      { key: 'push', icon: 'notifications-outline', label: 'Push Notifications', desc: 'Alerts on this device' },
      { key: 'email', icon: 'mail-outline', label: 'Email Notifications', desc: 'Receive notifications by email' },
    ],
  },
  {
    title: 'Activity',
    subtitle: 'People interacting with you',
    items: [
      { key: 'mentions', icon: 'at-outline', label: 'Mentions', desc: 'When someone mentions you' },
      { key: 'replies', icon: 'chatbubbles-outline', label: 'Replies', desc: 'When someone replies to your post' },
      { key: 'likes', icon: 'heart-outline', label: 'Likes', desc: 'When someone likes your post' },
      { key: 'follows', icon: 'person-add-outline', label: 'New Followers', desc: 'When someone follows you' },
      { key: 'directMessages', icon: 'chatbubble-outline', label: 'Direct Messages', desc: 'When someone messages you' },
    ],
  },
  {
    title: 'Communities',
    subtitle: 'Groups and spaces you follow',
    items: [
      { key: 'groupInvites', icon: 'people-circle-outline', label: 'Group Invites', desc: 'When someone invites you to a group' },
      { key: 'communityUpdates', icon: 'people-outline', label: 'Community Updates', desc: 'New posts in communities you follow' },
    ],
  },
];

export default function NotificationsScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<NotifSettings>({
    email: true,
    push: true,
    mentions: true,
    replies: true,
    likes: true,
    follows: true,
    directMessages: true,
    groupInvites: true,
    communityUpdates: true,
  });

  const fetchNotifSettings = useCallback(async () => {
    setLoading(true);
    const res = await api<NotifSettings>('/settings/notifications');
    if (res.success && res.data) {
      setSettings({
        email: res.data.email !== false,
        push: res.data.push !== false,
        mentions: res.data.mentions !== false,
        replies: res.data.replies !== false,
        likes: res.data.likes !== false,
        follows: res.data.follows !== false,
        directMessages: res.data.directMessages !== false,
        groupInvites: res.data.groupInvites !== false,
        communityUpdates: res.data.communityUpdates !== false,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchNotifSettings();
  }, [fetchNotifSettings]);

  async function handleToggle(key: keyof NotifSettings, value: boolean) {
    const prev = settings[key];
    // Functional updates so two quick toggles never clobber each other with a
    // stale snapshot (the old spread-of-`settings` did exactly that on revert).
    setSettings((s) => ({ ...s, [key]: value }));

    const res = await api('/settings/notifications', {
      method: 'PUT',
      body: JSON.stringify({ [key]: value }),
    });

    if (!res.success) {
      setSettings((s) => ({ ...s, [key]: prev }));
      Alert.alert('Error', res.error || 'Failed to update notification setting.');
    }
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, containerStyle]}
        showsVerticalScrollIndicator={false}
      >
        {NOTIF_GROUPS.map((group) => (
          <View key={group.title} style={styles.group}>
            <View style={styles.groupHeader}>
              <Text style={styles.sectionTitle}>{group.title}</Text>
              <Text style={styles.sectionSubtitle}>{group.subtitle}</Text>
            </View>
            <GlassCard padding={0}>
              {group.items.map((item, idx) => (
                <View
                  key={item.key}
                  style={[
                    styles.row,
                    idx < group.items.length - 1 && styles.rowBorder,
                  ]}
                >
                  <View style={styles.rowLeft}>
                    <View style={styles.iconWrap}>
                      <Ionicons
                        name={item.icon as any}
                        size={20}
                        color={colors.primary}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.label} numberOfLines={1}>{item.label}</Text>
                      <Text style={styles.desc} numberOfLines={2}>{item.desc}</Text>
                    </View>
                  </View>
                  <Switch
                    value={!!settings[item.key]}
                    onValueChange={(v) => handleToggle(item.key, v)}
                    trackColor={{ false: colors.faint, true: colors.primary + '60' }}
                    thumbColor={settings[item.key] ? colors.primary : colors.card}
                    accessibilityLabel={item.label}
                  />
                </View>
              ))}
            </GlassCard>
          </View>
        ))}

        <View style={{ height: spacing.xxxl }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    padding: spacing.lg,
  },
  group: {
    marginBottom: spacing.xl,
  },
  groupHeader: {
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: colors.faint,
    marginTop: 2,
    ...fonts.regular,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  rowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.medium,
  },
  desc: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    ...fonts.regular,
  },
});
